import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlusCircle, faTimes, faCheck } from "@fortawesome/free-solid-svg-icons";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "../../../components/ui";
import {
  CampaignAssetService,
  CreateCampaignAssetFromScratchRequest,
} from "../../../services/campaignAssetService";
import { CampaignAsset, AssetTableItem } from "../../../types/types";
import { AssetTypeOptions } from "../../../options/options";
import AssetMarkdownField from "../../assets/explorer/AssetMarkdownField";

interface CreateCampaignAssetModalProps {
  campaignId: number;
  existingCampaignAssets: CampaignAsset[];
  onCreated: (_asset: CampaignAsset) => void;
  onClose: () => void;
}

const CreateCampaignAssetModal: React.FC<CreateCampaignAssetModalProps> = ({
  campaignId,
  existingCampaignAssets,
  onCreated,
  onClose,
}) => {
  const [form, setForm] = useState<CreateCampaignAssetFromScratchRequest>({
    name: "",
    type: "",
    category: "",
    description: "",
    parentId: undefined,
    tags: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Convert existing campaign assets to AssetTableItem list for autocomplete
  const allAssets: AssetTableItem[] = existingCampaignAssets.map((ca) => ({
    ...ca.asset,
    id: ca.id, // use campaign-asset ID so [[asset:ID|Name]] refs resolve correctly
  }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!form.name.trim()) {
      setError("Name is required.");
      return;
    }
    if (!form.type) {
      setError("Type is required.");
      return;
    }
    if (!form.category.trim()) {
      setError("Category is required.");
      return;
    }

    setSaving(true);
    try {
      const created = await CampaignAssetService.createCampaignAssetFromScratch(campaignId, form);
      onCreated(created);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create asset.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} size="md">
      <ModalHeader onClose={onClose}>
        <div className="flex items-center gap-3">
          <FontAwesomeIcon icon={faPlusCircle} className="text-amber-400 text-lg" />
          <h2 className="text-lg font-light text-white tracking-wide">Create Chronicle Entry</h2>
        </div>
      </ModalHeader>

      <form onSubmit={handleSubmit} noValidate>
        <ModalBody>
          <div className="space-y-4">
            {error && (
              <div className="mb-4 px-3 py-2 bg-red-900/30 border border-red-800 text-red-300 text-sm flex items-start gap-2">
                <FontAwesomeIcon icon={faTimes} className="flex-shrink-0 mt-0.5" />
                {error}
              </div>
            )}

            {/* Name */}
            <div>
              <label className="block text-xs font-semibold text-amber-400 tracking-widest uppercase mb-1.5">
                Name *
              </label>
              <input
                type="text"
                className="w-full bg-zinc-800 border border-zinc-700 text-zinc-200 px-3 py-1.5 text-sm outline-none focus:border-amber-400 transition-colors rounded"
                placeholder="Asset name…"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                autoFocus
              />
            </div>

            {/* Type + Category row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-amber-400 tracking-widest uppercase mb-1.5">
                  Type *
                </label>
                <select
                  className="w-full bg-zinc-800 border border-zinc-700 text-zinc-200 px-3 py-1.5 text-sm outline-none focus:border-amber-400 transition-colors rounded"
                  value={form.type}
                  onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
                >
                  {AssetTypeOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-amber-400 tracking-widest uppercase mb-1.5">
                  Category *
                </label>
                <input
                  type="text"
                  className="w-full bg-zinc-800 border border-zinc-700 text-zinc-200 px-3 py-1.5 text-sm outline-none focus:border-amber-400 transition-colors rounded"
                  placeholder="e.g. Secret Society"
                  value={form.category}
                  onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                />
              </div>
            </div>

            {/* Parent */}
            <div>
              <label className="block text-xs font-semibold text-amber-400 tracking-widest uppercase mb-1.5">
                Parent Asset
              </label>
              <select
                className="w-full bg-zinc-800 border border-zinc-700 text-zinc-200 px-3 py-1.5 text-sm outline-none focus:border-amber-400 transition-colors rounded"
                value={form.parentId ?? ""}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    parentId: e.target.value ? Number(e.target.value) : undefined,
                  }))
                }
              >
                <option value="">None (root entry)</option>
                {existingCampaignAssets.map((ca) => (
                  <option key={ca.id} value={ca.id}>
                    {ca.asset.name} ({ca.asset.type})
                  </option>
                ))}
              </select>
            </div>

            {/* Description */}
            <div>
              <label className="block text-xs font-semibold text-amber-400 tracking-widest uppercase mb-1.5">
                Description
              </label>
              <AssetMarkdownField
                value={form.description!}
                onChange={(value) => setForm((f) => ({ ...f, description: value }))}
                allAssets={allAssets}
                onReferenceNavigate={() => {}}
              />
            </div>

            {/* Tags */}
            <div>
              <label className="block text-xs font-semibold text-amber-400 tracking-widest uppercase mb-1.5">
                Tags
              </label>
              <input
                type="text"
                className="w-full bg-zinc-800 border border-zinc-700 text-zinc-200 px-3 py-1.5 text-sm outline-none focus:border-amber-400 transition-colors rounded"
                placeholder="tag1, tag2, tag3…"
                value={form.tags}
                onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))}
              />
            </div>
          </div>
        </ModalBody>

        <ModalFooter>
          <span className="text-xs text-zinc-500">
            Fields marked <span className="text-red-400">*</span> are required
          </span>
          <div className="flex gap-3">
            <button
              type="button"
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm text-zinc-400 hover:text-white transition-colors"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-amber-400 text-zinc-950 font-medium hover:bg-amber-300 transition-colors disabled:opacity-60 rounded"
              disabled={saving}
            >
              <FontAwesomeIcon icon={faCheck} className="text-xs" />
              {saving ? "Creating…" : "Create Asset"}
            </button>
          </div>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default CreateCampaignAssetModal;
